<template>
  <div class="space-y-6">
    <div class="space-y-4">
      <!-- 题目卡片 -->
      <div
        v-for="(dim, index) in dimensions"
        :key="dim.id"
        :ref="setDimensionRef(index)"
        :style="{ animationDelay: index * 0.1 + 's' }"
        class="animate-slide-up"
        :class="[
          'relative p-5 rounded-xl border transition-all duration-300',
          answers[dim.id] !== undefined ? 'border-[#F2C94C]/30 bg-white' : 'border-slate-200 bg-white'
        ]"
      >
        <div class="mb-4 flex items-start justify-between">
          <div>
            <h3 class="text-lg font-bold text-slate-900 flex items-center gap-2">
              <span class="text-[#F2C94C]">0{{ index + 1 }}.</span>
              {{ dim.title }}
            </h3>
            <p class="text-slate-500 text-sm mt-1">{{ dim.description }}</p>
          </div>
          <span
            v-if="answers[dim.id] !== undefined"
            class="text-xs font-bold text-[#F2C94C] bg-[#F2C94C]/10 px-2 py-1 rounded"
          >
            已完成
          </span>
        </div>

        <div class="grid grid-cols-1 gap-2">
          <button
            v-for="option in dim.options"
            :key="option.label"
            @click="handleOptionSelect(dim.id, option.label, index)"
            :class="[
              'relative p-3 rounded-lg border-2 text-left transition-all group',
              answers[dim.id] === option.label
                ? 'border-[#F2C94C] bg-[#F2C94C]/5'
                : 'border-slate-100 bg-slate-50 hover:border-slate-300'
            ]"
          >
            <div class="flex items-start justify-between gap-3">
              <div class="flex-1">
                <span :class="[
                  'font-bold block mb-1',
                  answers[dim.id] === option.label ? 'text-slate-900' : 'text-slate-700'
                ]">
                  {{ option.label }}
                </span>
                <span class="text-xs text-slate-500 block">
                  {{ option.desc }}
                </span>
              </div>
              <div :class="[
                'w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all mt-0.5',
                answers[dim.id] === option.label ? 'border-[#F2C94C] bg-[#F2C94C]' : 'border-slate-200'
              ]">
                <div v-if="answers[dim.id] === option.label" class="w-2 h-2 bg-white rounded-full"></div>
              </div>
            </div>
          </button>
        </div>
      </div>

      <!-- 补充细节 -->
      <div
        ref="detailsRef"
        style="animation-delay: 0.3s"
        class="animate-slide-up p-5 rounded-xl border border-slate-200 bg-white"
      >
        <div class="mb-3">
          <h3 class="text-base font-bold text-slate-900 mb-1">
            请再补充一些您团队针对该管理痛点的细节情况？
          </h3>
          <p class="text-slate-500 text-xs">
            还有哪些只有你知道的现场细节？（选填）
          </p>
        </div>

        <textarea
          :value="details"
          @input="$emit('details-change', $event.target.value)"
          placeholder="例如：团队目前士气低落，连续三个月未达成目标，上周有两位核心成员提出了离职意向..."
          class="w-full h-24 bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-800 outline-none focus:border-[#F2C94C] focus:ring-2 focus:ring-[#F2C94C]/20 transition-all resize-none placeholder:text-slate-400"
        />
      </div>

      <!-- 占位空间，防止被固定按钮遮挡 -->
      <div class="h-24"></div>
    </div>

    <!-- 底部固定提交按钮 -->
    <div class="fixed bottom-0 left-0 right-0 md:left-64 bg-white border-t border-slate-200 px-6 py-4 z-20 shadow-lg">
      <div class="max-w-6xl mx-auto flex items-center justify-between">
        <div class="flex items-center gap-4">
          <button
            @click="onBack"
            class="hidden md:flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 transition-colors"
          >
            <IconArrowLeft class-name="w-4 h-4" />
            返回修改场景
          </button>
          <div class="text-sm text-slate-500">
            <span v-if="remainingCount > 0">
              还剩 <span class="font-bold text-[#F2C94C]">{{ remainingCount }}</span> 题未完成
            </span>
            <span v-else class="text-green-600 font-medium">✓ 所有题目已完成</span>
          </div>
        </div>
        <button
          @click="onSubmit"
          :disabled="!canSubmit"
          :class="[
            'px-8 py-3 rounded-xl font-bold flex items-center gap-2 transition-all',
            canSubmit
              ? 'bg-[#F2C94C] text-white shadow-md hover:bg-[#E5B73B] hover:shadow-lg'
              : 'bg-slate-100 text-slate-400 cursor-not-allowed'
          ]"
        >
          <template v-if="canSubmit">
            开始 AI 诊断
            <IconArrowRight class-name="w-4 h-4" />
          </template>
          <template v-else>
            还剩 {{ remainingCount }} 题
          </template>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { DIMENSIONS_MAP, getQuestionSetKey } from './questionsData';
import IconArrowLeft from '../../icons/IconArrowLeft.vue';
import IconArrowRight from '../../icons/IconArrowRight.vue';

export default {
  name: 'StepQuestions',
  components: {
    IconArrowLeft,
    IconArrowRight
  },
  props: {
    scenario: {
      type: String,
      default: ''
    },
    answers: {
      type: Object,
      default: () => ({})
    },
    details: {
      type: String,
      default: ''
    },
    canSubmit: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dimensionRefs: []
    };
  },
  computed: {
    questionSetKey() {
      return getQuestionSetKey(this.scenario);
    },
    dimensions() {
      return DIMENSIONS_MAP[this.questionSetKey] || [];
    },
    completedCount() {
      return Object.keys(this.answers).length;
    },
    remainingCount() {
      return this.dimensions.length - this.completedCount;
    }
  },
  mounted() {
    this.$nextTick(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  },
  methods: {
    setDimensionRef(index) {
      return (el) => {
        if (el) {
          this.dimensionRefs[index] = el;
        }
      };
    },
    handleOptionSelect(dimId, optionLabel, index) {
      const newAnswers = { ...this.answers, [dimId]: optionLabel };
      this.$emit('answers-change', newAnswers);

      // 自动滚动到下一题或补充细节
      this.$nextTick(() => {
        setTimeout(() => {
          if (index < this.dimensions.length - 1) {
            const nextEl = this.dimensionRefs[index + 1];
            if (nextEl) {
              nextEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          } else {
            const detailsEl = this.$refs.detailsRef;
            if (detailsEl) {
              detailsEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }
        }, 100);
      });
    },
    onBack() {
      this.$emit('back');
    },
    onSubmit() {
      this.$emit('submit');
    }
  }
};
</script>
